export const findLastTokenSale = (tokens: any[]): any['lastSale'] | null => {
    return tokens.reduce<any['lastSale'] | null>((lastSale, token) => {
        const currentSale = token.lastSale;
        if (!currentSale) {
            // nothign to compare
            return lastSale
        };

        // If we don't have a previous sale, then use the current one.
        if (!lastSale) {
            return currentSale
        };

        // Use a fallback timestamp (e.g., current time) if missing.
        const lastSaleTimestamp =
            lastSale.timestamp ?? Math.floor(Date.now() / 1000);
        const currentSaleTimestamp =
            currentSale.timestamp ?? Math.floor(Date.now() / 1000);

        return currentSaleTimestamp > lastSaleTimestamp ? currentSale : lastSale;
    }, null);
};

export const findTokenFloorPrice = (
    tokens: any[]
): {
    floorAsk: any | null;
    updatedTokenOwner: any | null;
    updatedContractId: any | null;
    updatedChainId: any | null;
} | null => {
    return tokens.reduce<{
        floorAsk: any | null;
        updatedTokenOwner: any | null;
        updatedContractId: any | null;
        updatedChainId: any | null;
    } | null>((best, token) => {
        const currentFloorAsk = token.market?.floorAsk;
        // Skip tokens without a valid floorAsk id.
        if (!currentFloorAsk?.id) return best;

        // If there's no best yet, use the current token's floorAsk.
        if (!best) {
            return {
                floorAsk: currentFloorAsk,
                updatedTokenOwner: token.token?.owner ?? null,
                updatedChainId: token.token?.chainId ?? null,
                updatedContractId: token.token?.contract ?? null,
            };
        }

        const currentPriceUSD = currentFloorAsk.price?.amount?.usd ?? Infinity;
        const bestPriceUSD = best.floorAsk?.price?.amount?.usd ?? Infinity;

        // Compare prices.
        if (currentPriceUSD < bestPriceUSD) {
            return {
                floorAsk: currentFloorAsk,
                updatedTokenOwner: token.token?.owner ?? null,
                updatedChainId: token.token?.chainId ?? null,
                updatedContractId: token.token?.contract ?? null,
            };
        } else if (currentPriceUSD > bestPriceUSD) {
            return best;
        } else {
            // Prices are equal; compare validUntil values.
            const currentValidUntil = currentFloorAsk.validUntil ?? 0;
            const bestValidUntil = best.floorAsk?.validUntil ?? 0;
            return currentValidUntil < bestValidUntil
                ? {
                    floorAsk: currentFloorAsk,
                    updatedTokenOwner: token.token?.owner ?? null,
                    updatedChainId: token.token?.chainId ?? null,
                    updatedContractId: token.token?.contract ?? null,
                }
                : best;
        }
    }, null);
};

export const findTokenTopBid = (
    tokens: any[]
): {
    topBid: any | null;
    updatedTokenOwner: any | null;
    updatedContractId: any | null;
    updatedChainId: any | null;
} | null => {
    return tokens.reduce<{
        topBid: any | null;
        updatedTokenOwner: any | null;
        updatedContractId: any | null;
        updatedChainId: any | null;
    } | null>((best, token) => {
        const currentTopBid = token.market?.topBid;
        // Skip tokens without a valid topBid id.
        if (!currentTopBid?.id) return best;

        // If there's no best candidate yet, use the current token's top bid.
        if (!best) {
            return {
                topBid: currentTopBid,
                updatedTokenOwner: token.token?.owner ?? null,
                updatedChainId: token.token?.chainId ?? null,
                updatedContractId: token.token?.contract ?? null,
            };
        }

        const currentPriceUSD = currentTopBid.price?.amount?.usd ?? -Infinity;
        const bestPriceUSD = best.topBid?.price?.amount?.usd ?? -Infinity;

        // For top bid, we want the highest USD price.
        if (currentPriceUSD > bestPriceUSD) {
            return {
                topBid: currentTopBid,
                updatedTokenOwner: token.token?.owner ?? null,
                updatedChainId: token.token?.chainId ?? null,
                updatedContractId: token.token?.contract ?? null,
            };
        } else if (currentPriceUSD < bestPriceUSD) {
            return best;
        } else {
            // Prices are equal; compare validUntil values.
            // According to the original code, we select the bid with the later validUntil.
            const currentValidUntil = currentTopBid.validUntil ?? 0;
            const bestValidUntil = best.topBid.validUntil ?? 0;
            return currentValidUntil > bestValidUntil
                ? {
                    topBid: currentTopBid,
                    updatedTokenOwner: token.token?.owner ?? null,
                    updatedChainId: token.token?.chainId ?? null,
                    updatedContractId: token.token?.contract ?? null,
                }
                : best;
        }
    }, null);
};

export function mergeTokenProperties(items: any[]): any {
    const merged = items.reduce<any>(
        (acc, { token }) => {
            const lastTokenSale = findLastTokenSale(Object.values(items));
            const tokenFloorPriceResult = findTokenFloorPrice(Object.values(items));
            const tokenTopBid = findTokenTopBid(Object.values(items));

            // Ensure tokenFloorPriceResult is not null before destructuring
            const {
                floorAsk: tokenFloorAskPrice,
                updatedTokenOwner: updatedFloorAskTokenOwner,
                updatedChainId: updatedTokenFloorChainId,
                updatedContractId: updatedTokenFloorContractId
            } = tokenFloorPriceResult || { floorAsk: null, updatedTokenOwner: null, updatedChainId: null, updatedContractId: null };

            const updatedSupply = Number(acc.token.supply ?? 0) + Number(token.supply);
            const updatedRemainingSupply =
                Number(acc.token.remainingSupply ?? 0) + Number(token.remainingSupply);

            return {
                token: {
                    tokenId: token.tokenId,
                    supply: updatedSupply,
                    remainingSupply: updatedRemainingSupply,
                    lastSale: lastTokenSale,
                    ...(updatedFloorAskTokenOwner !== null && { owner: updatedFloorAskTokenOwner }),
                    ...(updatedTokenFloorChainId !== null && { chainId: updatedTokenFloorChainId }),
                    ...(updatedTokenFloorContractId !== null && { contract: updatedTokenFloorContractId }),
                },
                market: {
                    floorAsk: tokenFloorAskPrice !== null ? tokenFloorAskPrice : null,
                    topBid: tokenTopBid
                },
                updatedAt: new Date().toISOString(),
            };
        },
        {
            token: { supply: 0, remainingSupply: 0, lastSale: null },
        }
    );
    return merged;
}

export function sortMergedTokens(tokens: any[], sortDirection: string = 'asc', sortBy: string = 'floorAskPrice'): any[] {
    // If sorting by tokenId, sort solely based on the tokenId
    if (sortBy === 'tokenId') {
        return tokens.sort((a, b) => {
            return sortDirection === 'asc'
                ? a.token.tokenId.localeCompare(b.token.tokenId)
                : b.token.tokenId.localeCompare(a.token.tokenId);
        });
    }

    // Otherwise, use the original floorAskPrice sorting logic
    return tokens.sort((a, b) => {
        const aFloorAsk = a.market?.floorAsk;
        const bFloorAsk = b.market?.floorAsk;

        // If both tokens have floorAsk, compare by price.amount.usd in the specified order.
        if (aFloorAsk && bFloorAsk) {
            const aPrice = Number(aFloorAsk.price?.amount?.usd ?? Infinity);
            const bPrice = Number(bFloorAsk.price?.amount?.usd ?? Infinity);
            if (aPrice !== bPrice) {
                return sortDirection === 'asc' ? aPrice - bPrice : bPrice - aPrice;
            }
            // If prices are equal, fallback to tokenId
            return a.token.tokenId.localeCompare(b.token.tokenId);
        }

        // If only one token has a floorAsk, it should come first.
        if (aFloorAsk && !bFloorAsk) {
            return -1;
        }
        if (!aFloorAsk && bFloorAsk) {
            return 1;
        }

        // If neither token has a floorAsk, sort by tokenId respecting sort direction
        return sortDirection === 'asc'
            ? a.token.tokenId.localeCompare(b.token.tokenId)
            : b.token.tokenId.localeCompare(a.token.tokenId);
    });
}

export const groupTokenIds = (allDynamicTokens: any[]) => {
    const groupedMap = new Map<string, any[]>();

    for (const item of allDynamicTokens.flat()) {
        if (!item?.token) return {};

        const key = item.token.tokenId;
        const group = groupedMap.get(key) || [];
        group.push(item);
        groupedMap.set(key, group);
    }

    return Object.fromEntries(groupedMap);
}

export const sortTokenData = (tokens: any[], sortDirection: string = 'asc', sortBy: string = 'floorAskPrice') => {
    const groupedTokenIds = groupTokenIds(tokens);
    // Loop through all grouped token Ids to merge the data
    // Loop over the grouped tokens.
    const allMergedTokenData: any[] = [];

    Object.entries(groupedTokenIds).forEach(([tokenId, items]) => {
        // Type assertion: items is of type Item[]
        const tokenItems = items as any[];

        if (tokenItems.length > 1) {
            // More than one item with the same tokenId, so merge their properties.
            const mergedToken = mergeTokenPropertiesV2(tokenItems);
            allMergedTokenData.push(mergedToken);
        } else {
            // Only one item; optionally process it as is.
            allMergedTokenData.push(items[0]);
        }
    });
    // Sort grouped + merged token Ids by floorAskPrice, asc (by default)
    return sortMergedTokens(allMergedTokenData, sortDirection, sortBy);
}

export function mergeTokenPropertiesV2(items: any[]): any {
    // 1. Compute global properties (last sale and best floor ask details)
    const lastTokenSale = findLastTokenSale(items);
    const tokenFloorPriceResult = findTokenFloorPrice(items);
    const tokenTopBid = findTokenTopBid(items);
    const {
        floorAsk: tokenFloorAskPrice,
        updatedTokenOwner: updatedFloorAskTokenOwner,
        updatedChainId: updatedTokenFloorChainId,
        updatedContractId: updatedTokenFloorContractId,
    } = tokenFloorPriceResult || {
        floorAsk: null,
        updatedTokenOwner: null,
        updatedChainId: null,
        updatedContractId: null,
    };

    // 2. Accumulate the supply and remainingSupply values from all tokens.
    const { totalSupply, totalRemainingSupply } = items.reduce(
        (acc, { token }) => ({
            totalSupply: acc.totalSupply + Number(token.supply ?? 0),
            totalRemainingSupply:
                acc.totalRemainingSupply + Number(token.remainingSupply ?? 0),
        }),
        { totalSupply: 0, totalRemainingSupply: 0 }
    );

    // 3. Select the reference token based on the updated chainId.
    //    If no best floor ask was found (and thus no updatedChainId), fall back to the first token.
    const referenceToken =
        updatedTokenFloorChainId !== null
            ? items.find((item) => item.token.chainId === updatedTokenFloorChainId)
                ?.token
            : items[0].token;

    // 4. Merge all the properties into one final token object.
    //    The accumulated supply values override the referenceToken's supply data.
    const mergedToken = {
        ...referenceToken,
        supply: totalSupply,
        remainingSupply: totalRemainingSupply,
        lastSale: lastTokenSale,
        ...(updatedFloorAskTokenOwner !== null && {
            owner: updatedFloorAskTokenOwner,
        }),
        ...(updatedTokenFloorChainId !== null && {
            chainId: updatedTokenFloorChainId,
        }),
        ...(updatedTokenFloorContractId !== null && {
            contract: updatedTokenFloorContractId,
        }),
    };

    return {
        token: mergedToken,
        market: {
            floorAsk: tokenFloorAskPrice !== null ? tokenFloorAskPrice : null,
            topBid: tokenTopBid?.topBid,
        },
        updatedAt: new Date().toISOString(),
    };
}

export const sortByPriceAndTime = (a: any, b: any) => {
    const priceA = a?.price?.amount?.raw || 0;
    const priceB = b?.price?.amount?.raw || 0;

    if (priceA !== priceB) {
        return priceA - priceB;
    }

    const dateA = new Date(a?.updatedAt || 0).getTime();
    const dateB = new Date(b?.updatedAt || 0).getTime();
    return dateA - dateB;
};

export const sortTokenFloorListings = (tokenListings: any[], walletAddress?: string) => {
    if (!tokenListings?.length) {
        return []
    };

    const filteredListings = walletAddress
        ? tokenListings.filter(
            (listing) =>
                listing.maker.toLowerCase() !== walletAddress.toLowerCase()
        )
        : tokenListings;

    return filteredListings.sort(sortByPriceAndTime);
}
